"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AdornedInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _deleteUndefined = require("../../util/deleteUndefined");
var _identifier = require("../../util/identifier");
var _FormField = require("../FormField/FormField.context");
var _AdornedInput = require("./AdornedInput.styles");
var _excluded = ["disabled", "error", "input", "inputId", "leftAdornment", "readOnly", "rightAdornment", "size"];
/**
 * @since 5.0.0
 */
var AdornedInput = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var disabled = props.disabled,
    error = props.error,
    input = props.input,
    inputId = props.inputId,
    leftAdornment = props.leftAdornment,
    readOnly = props.readOnly,
    rightAdornment = props.rightAdornment,
    size = props.size,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var context = (0, _react.useContext)(_FormField.FormFieldContext);
  var defaultId = (0, _react.useRef)((0, _identifier.identifier)()).current;
  var id = inputId || context.controlId || defaultId;
  var leftAdornmentId = (0, _react.useRef)((0, _identifier.identifier)()).current;
  var rightAdornmentId = (0, _react.useRef)((0, _identifier.identifier)()).current;

  // Generate the labelledby ids
  var labels = [];
  if (context.labelId) {
    labels.push(context.labelId);
  }
  if (leftAdornment) {
    labels.push(leftAdornmentId);
  }
  if (rightAdornment) {
    labels.push(rightAdornmentId);
  }

  // These props must be the same for both the input and the adornments. Don't allow consumers
  // to manually override these, i.e. make them set these on the top level AdornedInput.
  var coordinatedProps = (0, _deleteUndefined.deleteUndefined)({
    size: size,
    error: typeof error !== 'undefined' ? error : context.error,
    disabled: typeof disabled !== 'undefined' ? disabled : context.disabled,
    readOnly: typeof readOnly !== 'undefined' ? readOnly : context.readOnly
  });

  // For when we need to pass required combobox aria tags down to the Input
  // docs: https://www.w3.org/TR/wai-aria-1.1/#combobox
  // ticket: https://zbrt.atl.zillow.net/browse/CDS-1171
  var filteredAriaProps = ['aria-expanded', 'aria-controls'];
  var inputAriaProps = {};
  var wrapperFilteredAriaProps = ['aria-expanded'];
  var wrapperAriaProps = Object.assign({}, props);
  if (typeof input.props.role !== 'undefined' && input.props.role === 'combobox') {
    inputAriaProps = Object.keys(Object.assign({}, rest)).reduce(function (accum, prop) {
      var runner = Object.assign({}, accum);
      if (filteredAriaProps.includes(prop)) {
        runner[prop] = rest[prop];
      }
      return runner;
    }, {});
    wrapperFilteredAriaProps.forEach(function (prop) {
      delete wrapperAriaProps[prop];
    });
  }
  return (
    /*#__PURE__*/
    // Note: Input component needs to be first in the DOM tree so that
    // Adornments can trigger off :hover/:focus styles.
    _react.default.createElement(_AdornedInput.StyledAdornedInput, (0, _extends2.default)({
      ref: forwardedRef
    }, wrapperAriaProps, coordinatedProps), input && /*#__PURE__*/(0, _react.cloneElement)(input, Object.assign({
      id: id,
      // aria-labelledby is only necessary when there are multiple labels
      // https://dequeuniversity.com/rules/axe/3.3/form-field-multiple-labels
      'aria-labelledby': labels.length > 1 ? labels.join(' ') : undefined
    }, inputAriaProps, input.props, coordinatedProps)), leftAdornment && /*#__PURE__*/(0, _react.cloneElement)(leftAdornment, Object.assign({
      htmlFor: id,
      id: leftAdornmentId
    }, leftAdornment.props, coordinatedProps, {
      position: 'left'
    })), rightAdornment && /*#__PURE__*/(0, _react.cloneElement)(rightAdornment, Object.assign({
      htmlFor: id,
      id: rightAdornmentId
    }, rightAdornment.props, coordinatedProps, {
      position: 'right'
    })))
  );
});
exports.AdornedInput = AdornedInput;
AdornedInput.displayName = 'AdornedInput';
AdornedInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Takes an Adornment component. The `position` prop will automatically be set to `left`.
   */
  leftAdornment: _propTypes.default.node,
  /**
   * Takes an Adornment component. The `position` prop will automatically be set to `right`.
   */
  rightAdornment: _propTypes.default.node,
  /**
   * Takes an Input component.
   */
  input: _propTypes.default.element.isRequired,
  /**
   * This will set the `id` of the nested Input as well as the `for` attribute for any
   * nested Adornments. If not provided, an id will be automatically generated.
   */
  inputId: _propTypes.default.string,
  /**
   * Displays the Input in an error state. Can also be inherited from a FormField parent.
   */
  error: _propTypes.default.bool,
  /**
   * This will set the `size` prop for the nested Input.
   */
  size: _propTypes.default.string,
  /**
   * Displays the Input and Adornments in a disabled state. Can also be inherited from a
   * FormField parent.
   */
  disabled: _propTypes.default.bool,
  /**
   * This will set the `fluid` prop on the nested Input.
   * Inputs are fluid by default which means they stretch to fill the entire width of their
   * container. When `fluid="false"`, the Input's width is set to `auto`.
   */
  fluid: _propTypes.default.bool,
  /**
   * Displays the Input in an error state. Can also be inherited from a FormField parent.
   *
   * @since 8.27.0
   */
  readOnly: _propTypes.default.bool
} : {};
AdornedInput.defaultProps = {
  fluid: true
};